import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Facilities", href: "#facilities" },
  { label: "Products", href: "#products" },
  { label: "Quality", href: "#quality" },
  { label: "Logistics", href: "#logistics" },
  { label: "Leadership", href: "#leadership" },
  { label: "Customers", href: "#clients" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 bg-white border-b border-amber-200/70 transition-all duration-300 ${
        scrolled ? "shadow-[0_6px_24px_rgba(11,46,89,0.10)]" : "shadow-[0_4px_18px_rgba(11,46,89,0.06)]"
      }`}
    >
      <div className="pl-3 pr-2 sm:pl-4 sm:pr-3 lg:pl-6 lg:pr-4">
        <div
          className={`flex items-center justify-between gap-4 transition-all duration-300 ${
            scrolled ? "h-16 lg:h-[72px]" : "h-20 lg:h-24"
          }`}
        >
          {/* Logo on the left */}
          <a
            href="#home"
            data-testid="site-logo"
            aria-label="Tirupati Global Sourcing Company — Home"
            className="flex items-center h-full py-1.5"
          >
            <img
              src="/logo-v2.png"
              alt="Tirupati Global Sourcing Company"
              className="h-full w-auto object-contain"
            />
          </a>

          {/* Nav on the right (same level) */}
          <nav
            className="hidden lg:flex items-center gap-3 xl:gap-4"
            aria-label="Primary"
          >
            {NAV.map((n) => (
              <a
                key={n.label}
                data-testid={`nav-${n.label.toLowerCase()}`}
                href={n.href}
                className="text-[11px] xl:text-[12px] font-bold text-brand-navy hover:text-brand-orange transition-colors uppercase tracking-[0.06em]"
              >
                {n.label}
              </a>
            ))}
            <a
              data-testid="nav-cta-rfq"
              href="#rfq"
              className="inline-flex items-center gap-1 text-[10.5px] xl:text-[11.5px] font-bold uppercase tracking-[0.05em] bg-brand-navy text-white px-3 py-2 hover:bg-brand-blue transition-colors whitespace-nowrap ml-1.5"
            >
              Request RFQ <ArrowUpRight className="h-3 w-3" />
            </a>
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 text-brand-navy"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden border-t border-amber-200/70 bg-white"
          >
            <nav className="container-x py-5 flex flex-col gap-4">
              {NAV.map((n) => (
                <a
                  key={n.label}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-semibold text-brand-navy"
                >
                  {n.label}
                </a>
              ))}
              <a
                data-testid="mobile-cta-rfq"
                href="#rfq"
                className="btn-primary mt-2"
                onClick={() => setOpen(false)}
              >
                Request RFQ
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
